// ==================== FEATURES PAGE ====================

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  MessageSquare,
  Users,
  Zap,
  BarChart3,
  Bot,
  Shield,
  Clock,
  Globe,
  Layers,
  Workflow,
  BrainCircuit,
  Send,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Target,
  Bell,
  FileText,
  Palette,
  Link2,
  Database,
  Lock,
  HeadphonesIcon,
  TrendingUp
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ROUTES } from '../../constants';
import {
  fadeInUp,
  fadeInLeft,
  fadeInRight,
  staggerContainer,
  scaleIn,
  hoverLift
} from '../../utils/animations';

// Core Features Data
const coreFeatures = [
  {
    icon: MessageSquare,
    title: 'Smart Messaging',
    description: 'Send personalized messages at scale with intelligent automation and dynamic content.',
    color: 'orange',
    features: ['Personalization tokens', 'Media messages', 'Quick replies', 'Message templates']
  },
  {
    icon: Users,
    title: 'Contact Management',
    description: 'Organize, segment, and manage your contacts with powerful filtering and tagging.',
    color: 'amber',
    features: ['Smart segmentation', 'Custom fields', 'Import/Export', 'Tags & Groups']
  },
  {
    icon: Layers,
    title: 'Campaign Builder',
    description: 'Create and schedule powerful marketing campaigns with our visual campaign builder.',
    color: 'orange',
    features: ['Visual builder', 'A/B testing', 'Scheduling', 'Templates library']
  },
  {
    icon: BarChart3,
    title: 'Advanced Analytics',
    description: 'Track performance with real-time dashboards, detailed reports, and actionable insights.',
    color: 'amber',
    features: ['Real-time stats', 'Custom reports', 'ROI tracking', 'Export data']
  },
  {
    icon: Workflow,
    title: 'Automation Flows',
    description: 'Build automated workflows triggered by customer actions and behaviors.',
    color: 'orange',
    features: ['Visual flow builder', 'Triggers & actions', 'Conditional logic', 'Time delays']
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'Bank-grade encryption and compliance with global privacy standards.',
    color: 'amber',
    features: ['End-to-end encryption', 'GDPR compliant', 'Role-based access', '2FA support']
  },
];

// Advanced Features
const advancedFeatures = [
  {
    icon: BrainCircuit,
    title: 'AI-Powered Responses',
    description: 'Leverage artificial intelligence to generate smart, contextual responses automatically.',
    badge: 'AI'
  },
  {
    icon: Bot,
    title: 'Chatbot Builder',
    description: 'Create intelligent chatbots without coding using our drag-and-drop builder.',
    badge: 'Popular'
  },
  {
    icon: Clock,
    title: 'Smart Scheduling',
    description: 'Schedule messages for optimal delivery times based on recipient timezone.',
    badge: null
  },
  {
    icon: Globe,
    title: 'Multi-Language',
    description: 'Reach global audiences with automatic language detection and translation.',
    badge: null
  },
  {
    icon: Bell,
    title: 'Real-time Notifications',
    description: 'Get instant alerts for important events and message deliveries.',
    badge: null
  },
  {
    icon: FileText,
    title: 'Template Library',
    description: 'Access 100+ pre-built templates for various industries and use cases.',
    badge: '100+'
  },
  {
    icon: Palette,
    title: 'Custom Branding',
    description: 'Customize the look and feel to match your brand identity.',
    badge: null
  },
  {
    icon: Link2,
    title: 'Webhooks & API',
    description: 'Connect with any system using our powerful REST API and webhooks.',
    badge: 'Dev'
  },
];

// Integration Partners
const integrations = [
  { name: 'Shopify', category: 'E-commerce' },
  { name: 'WooCommerce', category: 'E-commerce' },
  { name: 'Salesforce', category: 'CRM' },
  { name: 'HubSpot', category: 'CRM' },
  { name: 'Zapier', category: 'Automation' },
  { name: 'Make', category: 'Automation' },
  { name: 'Slack', category: 'Communication' },
  { name: 'Google Sheets', category: 'Productivity' },
];

// Stats
const stats = [
  { value: '99.9%', label: 'Uptime SLA', icon: TrendingUp },
  { value: '<100ms', label: 'API Latency', icon: Zap },
  { value: '24/7', label: 'Support', icon: HeadphonesIcon },
  { value: 'GDPR', label: 'Compliant', icon: Lock },
];

const FeaturesPage = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-linear-to-b from-(--color-midnight-navy) via-(--color-dark-navy) to-(--color-midnight-navy)" />
        <motion.div 
          className="absolute top-20 right-1/4 w-[500px] h-[500px] bg-(--color-signal-orange)/8 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-(--color-warm-amber)/8 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, repeat: Infinity }}
        />

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(230, 142, 55, 0.3) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(230, 142, 55, 0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <Badge variant="orange" className="mb-6">
                <Sparkles size={14} className="mr-1" />
                Powerful Features
              </Badge>
            </motion.div>
            
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6"
            >
              Everything You Need to
              <span className="text-gradient block mt-2">Scale Communications</span>
            </motion.h1>
            
            <motion.p
              variants={fadeInUp}
              className="text-lg md:text-xl text-(--color-soft-gray) leading-relaxed mb-10 max-w-2xl mx-auto"
            >
              Discover our comprehensive suite of tools designed to help businesses 
              amplify their customer conversations and drive measurable growth.
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to={ROUTES.AUTH.REGISTER}>
                <Button variant="primary" size="lg" rightIcon={<ArrowRight size={18} />}>
                  Get Started Free
                </Button>
              </Link>
              <Link to={ROUTES.CONTACT}>
                <Button variant="secondary" size="lg">
                  Talk to Sales
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-12 bg-(--color-dark-navy)">
        <div className="container-custom">
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                className="flex items-center gap-4 justify-center"
              >
                <div className="w-12 h-12 rounded-xl bg-(--color-orange-glow) flex items-center justify-center shrink-0">
                  <stat.icon size={22} className="text-(--color-signal-orange)" />
                </div>
                <div>
                  <div className="text-2xl lg:text-3xl font-heading font-bold text-white">
                    {stat.value}
                  </div>
                  <div className="text-sm text-(--color-muted-gray)">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Core Features Grid */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Badge variant="orange" className="mb-4">Core Features</Badge>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Built for Modern
              <span className="text-gradient"> Business Communication</span>
            </h2>
            <p className="text-(--color-muted-gray) text-lg">
              Powerful tools that work together seamlessly to transform your customer engagement.
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {coreFeatures.map((feature, index) => (
              <motion.div key={index} variants={fadeInUp} whileHover={hoverLift}>
                <Card hoverable className="h-full p-6 lg:p-8 group">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 ${
                    feature.color === 'orange' 
                      ? 'bg-(--color-orange-glow) group-hover:bg-(--color-signal-orange)/30' 
                      : 'bg-(--color-warm-amber)/20 group-hover:bg-(--color-warm-amber)/30'
                  }`}>
                    <feature.icon size={28} className={
                      feature.color === 'orange' 
                        ? 'text-(--color-signal-orange)' 
                        : 'text-(--color-warm-amber)'
                    } />
                  </div>
                  
                  <h3 className="text-xl font-heading font-semibold text-white mb-3 group-hover:text-(--color-signal-orange) transition-colors">
                    {feature.title}
                  </h3>
                  
                  <p className="text-(--color-muted-gray) leading-relaxed mb-5">
                    {feature.description}
                  </p>
                  
                  <ul className="space-y-2">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-(--color-soft-gray)">
                        <CheckCircle2 size={14} className="text-(--color-signal-orange) shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Feature Showcase - Messaging */}
      <section className="py-20 lg:py-28 bg-(--color-dark-navy)">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
            >
              <Badge variant="orange" className="mb-4">Smart Messaging</Badge>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-6">
                Send the Right Message
                <span className="text-gradient"> at the Right Time</span>
              </h2>
              
              <p className="text-(--color-soft-gray) text-lg leading-relaxed mb-8">
                Our intelligent messaging engine ensures your messages reach the right audience 
                with personalized content that drives engagement and conversions.
              </p>
              
              <ul className="space-y-4">
                {[
                  { icon: Target, text: 'Smart audience targeting based on behavior' },
                  { icon: Clock, text: 'Optimal send time prediction using AI' },
                  { icon: Sparkles, text: 'Dynamic content personalization' },
                  { icon: Send, text: 'Multi-channel delivery (WhatsApp, SMS, Email)' },
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-(--color-orange-glow) flex items-center justify-center shrink-0">
                      <item.icon size={18} className="text-(--color-signal-orange)" />
                    </div>
                    <span className="text-(--color-soft-gray)">{item.text}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
              className="relative"
            >
              {/* Mock UI Preview */}
              <div className="relative bg-(--color-charcoal-black) rounded-2xl p-6 border border-(--color-border-gray)">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500/60" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                  <div className="w-3 h-3 rounded-full bg-green-500/60" />
                </div>
                
                {/* Message Preview Cards */}
                <div className="space-y-3">
                  <motion.div
                    className="bg-(--color-midnight-navy) rounded-xl p-4 border border-(--color-border-gray)/50"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-(--color-signal-orange)/20 flex items-center justify-center">
                        <MessageSquare size={14} className="text-(--color-signal-orange)" />
                      </div>
                      <span className="text-sm text-white font-medium">Campaign: Welcome Series</span>
                      <Badge variant="success" size="sm" className="ml-auto">Delivered</Badge>
                    </div>
                    <p className="text-sm text-(--color-muted-gray)">
                      Hi {"{{name}}"}, welcome to AmplifyMsg! 🎉
                    </p>
                  </motion.div>
                  
                  <motion.div
                    className="bg-(--color-midnight-navy) rounded-xl p-4 border border-(--color-border-gray)/50"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 3, delay: 0.5, repeat: Infinity }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-(--color-warm-amber)/20 flex items-center justify-center">
                        <Bell size={14} className="text-(--color-warm-amber)" />
                      </div>
                      <span className="text-sm text-white font-medium">Campaign: Flash Sale</span>
                      <Badge variant="warning" size="sm" className="ml-auto">Sending</Badge>
                    </div>
                    <p className="text-sm text-(--color-muted-gray)">
                      🔥 24-Hour Flash Sale! Get 40% off...
                    </p>
                  </motion.div>
                  
                  <motion.div
                    className="bg-(--color-midnight-navy) rounded-xl p-4 border border-(--color-border-gray)/50"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 3, delay: 1, repeat: Infinity }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Users size={14} className="text-green-400" />
                      </div>
                      <span className="text-sm text-white font-medium">Campaign: Re-engagement</span>
                      <Badge variant="info" size="sm" className="ml-auto">Scheduled</Badge>
                    </div>
                    <p className="text-sm text-(--color-muted-gray)">
                      We miss you, {"{{name}}"}! Come back...
                    </p>
                  </motion.div>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-(--color-signal-orange)/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-(--color-warm-amber)/10 rounded-full blur-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Showcase - Automation */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInLeft}
              className="order-2 lg:order-1 relative"
            >
              {/* Automation Flow Preview */}
              <div className="relative bg-(--color-charcoal-black) rounded-2xl p-6 border border-(--color-border-gray)">
                <div className="absolute top-4 right-4">
                  <Badge variant="success" size="sm">Active Flow</Badge>
                </div>
                
                <h4 className="text-white font-medium mb-6">Welcome Automation Flow</h4>
                
                <div className="space-y-4">
                  {/* Flow Node 1 */}
                  <motion.div
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-(--color-signal-orange) flex items-center justify-center shrink-0">
                      <Zap size={20} className="text-white" />
                    </div>
                    <div className="flex-1 bg-(--color-midnight-navy) rounded-lg p-3 border border-(--color-border-gray)/50">
                      <p className="text-sm text-white font-medium">Trigger: New Contact Added</p>
                    </div>
                  </motion.div>
                  
                  {/* Connector */}
                  <div className="flex justify-start pl-5">
                    <div className="w-0.5 h-6 bg-(--color-signal-orange)/50" />
                  </div>
                  
                  {/* Flow Node 2 */}
                  <motion.div
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-(--color-warm-amber) flex items-center justify-center shrink-0">
                      <Clock size={20} className="text-white" />
                    </div>
                    <div className="flex-1 bg-(--color-midnight-navy) rounded-lg p-3 border border-(--color-border-gray)/50">
                      <p className="text-sm text-white font-medium">Wait: 5 minutes</p>
                    </div>
                  </motion.div>
                  
                  {/* Connector */}
                  <div className="flex justify-start pl-5">
                    <div className="w-0.5 h-6 bg-(--color-warm-amber)/50" />
                  </div>
                  
                  {/* Flow Node 3 */}
                  <motion.div
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center shrink-0">
                      <Send size={20} className="text-white" />
                    </div>
                    <div className="flex-1 bg-(--color-midnight-navy) rounded-lg p-3 border border-(--color-border-gray)/50">
                      <p className="text-sm text-white font-medium">Action: Send Welcome Message</p>
                    </div>
                  </motion.div>
                </div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-(--color-warm-amber)/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-(--color-signal-orange)/10 rounded-full blur-2xl" />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInRight}
              className="order-1 lg:order-2"
            >
              <Badge variant="orange" className="mb-4">Automation</Badge>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-6">
                Automate Your
                <span className="text-gradient"> Customer Journey</span>
              </h2>
              
              <p className="text-(--color-soft-gray) text-lg leading-relaxed mb-8">
                Build powerful automation flows that respond to customer behavior, 
                saving time while delivering personalized experiences at scale.
              </p>
              
              <ul className="space-y-4">
                {[
                  { icon: Workflow, text: 'Visual drag-and-drop flow builder' },
                  { icon: Zap, text: 'Event-based triggers and actions' },
                  { icon: BrainCircuit, text: 'AI-powered decision making' },
                  { icon: BarChart3, text: 'Flow performance analytics' },
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-(--color-orange-glow) flex items-center justify-center shrink-0">
                      <item.icon size={18} className="text-(--color-signal-orange)" />
                    </div>
                    <span className="text-(--color-soft-gray)">{item.text}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advanced Features Grid */}
      <section className="py-20 lg:py-28 bg-(--color-dark-navy)">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Badge variant="orange" className="mb-4">Advanced Features</Badge>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              More Power at
              <span className="text-gradient"> Your Fingertips</span>
            </h2>
            <p className="text-(--color-muted-gray) text-lg">
              Unlock advanced capabilities to take your messaging to the next level.
            </p>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {advancedFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="group"
              >
                <div className="relative h-full bg-(--color-charcoal-black) rounded-xl p-5 border border-(--color-border-gray) hover:border-(--color-signal-orange)/50 transition-all duration-300">
                  {feature.badge && (
                    <Badge 
                      variant={feature.badge === 'AI' ? 'orange' : feature.badge === 'Popular' ? 'success' : 'info'} 
                      size="sm"
                      className="absolute top-4 right-4"
                    >
                      {feature.badge}
                    </Badge>
                  )}
                  
                  <div className="w-11 h-11 rounded-lg bg-(--color-orange-glow) flex items-center justify-center mb-4 group-hover:bg-(--color-signal-orange)/30 transition-colors">
                    <feature.icon size={22} className="text-(--color-signal-orange)" />
                  </div>
                  
                  <h3 className="text-base font-heading font-semibold text-white mb-2 group-hover:text-(--color-signal-orange) transition-colors">
                    {feature.title}
                  </h3>
                  
                  <p className="text-sm text-(--color-muted-gray) leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Badge variant="orange" className="mb-4">Integrations</Badge>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
              Connect with Your
              <span className="text-gradient"> Favorite Tools</span>
            </h2>
            <p className="text-(--color-muted-gray) text-lg">
              Seamlessly integrate AmplifyMsg with the tools you already use.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {integrations.map((integration, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ scale: 1.05 }}
                className="bg-(--color-charcoal-black) rounded-xl p-5 border border-(--color-border-gray) hover:border-(--color-signal-orange)/50 transition-all duration-300 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-(--color-orange-glow) flex items-center justify-center">
                  <Database size={22} className="text-(--color-signal-orange)" />
                </div>
                <h5 className="text-white font-medium mb-1">{integration.name}</h5>
                <p className="text-xs text-(--color-muted-gray)">{integration.category}</p>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-linear-to-r from-(--color-signal-orange)/20 via-(--color-midnight-navy) to-(--color-warm-amber)/20" />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-(--color-signal-orange)/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6"
            >
              Ready to Experience
              <span className="text-gradient block mt-2">These Features?</span>
            </motion.h2>
            
            <motion.p
              variants={fadeInUp}
              className="text-lg text-(--color-soft-gray) mb-10"
            >
              Join thousands of businesses already using AmplifyMsg to transform 
              their customer communications.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to={ROUTES.AUTH.REGISTER}>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="primary" size="lg" rightIcon={<ArrowRight size={18} />}>
                    Get Started Now
                  </Button>
                </motion.div>
              </Link>
              <Link to={ROUTES.PRICING}>
                <Button variant="secondary" size="lg">
                  View Pricing
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export { FeaturesPage };

